import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { filter, map, take, tap } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';

import { ResourceService } from '@global/infrastructure/abstracts/resource/resource.service';

import { Environment } from '@global/infrastructure/models/environment';
import { LoadPolicyConstants } from '@global/infrastructure/constants/load-policy';
import { Utility } from '@global/infrastructure/helpers/utility';

@Injectable({ providedIn: 'root' })
export class SessionService extends ResourceService<any> {
  constructor(httpClient: HttpClient, env: Environment) {
    super(httpClient, env, `sessions?app=${Utility.isMta() ? 'stp' : 'ren'}`);
  }

  private readonly SESSION_KEY = 'app-sessionid';

  getSessionExpirySeconds$ = timer(1000, 1000).pipe(
    map(() => {
      const sessionFromStorage = this.getSessionFromStorage();
      if (sessionFromStorage) {
        return {
          sessionMins: sessionFromStorage?.sessionMins,
          warningMins: sessionFromStorage?.warningMins,
          secondsRemaining: this.getSecondsRemaining(sessionFromStorage),
          sessionId: sessionFromStorage?.sessionId,
        };
      } else {
        return { secondsRemaining: 1, sessionMins: 0, warningMins: 0 };
      }
    }),
    filter(
      (session) =>
        session.secondsRemaining <=
        (session.sessionMins - session.warningMins) * 60
    ),
    take(1)
  );

  public static clearSession() {
    const sessionValueToKeep = sessionStorage.getItem('app-renewal-mta-details');
    sessionStorage.clear();
    if (sessionValueToKeep !== null) {
      sessionStorage.setItem('app-renewal-mta-details', sessionValueToKeep);
    }
  }

  refreshSession(sessionId): Observable<string> {
    return this.refresh(sessionId).pipe(
      tap((data) => {
        this.keepAlive().pipe(take(1)).subscribe();
        this.setSessionToStorage(data);
      })
    );
  }

  getSession(): Observable<string> {
    const sessionData = this.getSessionFromStorage();
    return sessionData ? of(sessionData.sessionId) : this.createSession();
  }

  private getSessionFromStorage(): any | null {
    const sessionObj = sessionStorage.getItem(this.SESSION_KEY);
    return !!sessionObj ? JSON.parse(sessionObj) : null;
  }

  private setSessionToStorage(data: any) {
    sessionStorage.removeItem(this.SESSION_KEY);
    sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
  }

  private createSession(): Observable<string> {
    return this.create(null, false).pipe(
      tap((data) => this.setSessionToStorage(data)),
      map((data) => data.sessionId)
    );
  }

  private getSecondsRemaining(session) {
    return session?.expiresAt
      ? Math.floor((session?.expiresAt * 1000 - Date.now()) / 1000)
      : LoadPolicyConstants.SESSION_SECONDS;
  }
}
